import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_BASE || 'http://localhost:9090';

interface ChatroomSocketHookOptions {
  userId?: string;
  onNewMessage?: (data: any) => void;
  onMessageEdited?: (data: any) => void;
  onMessageRemoved?: (data: any) => void;
  onMemberAdded?: (data: any) => void;
  onMemberRemoved?: (data: any) => void;
  onUserTyping?: (data: any) => void;
  onUserJoined?: (data: any) => void;
  onUserLeft?: (data: any) => void;
  onChatroomArchived?: (data: any) => void;
  onChatroomUnarchived?: (data: any) => void;
  onSettingsUpdated?: (data: any) => void;
  onUnreadUpdated?: (data: any) => void;
  onPresenceUpdate?: (data: any) => void;
}

export function useChatroomSocket(options: ChatroomSocketHookOptions) {
  const socketRef = useRef<Socket | null>(null);
  const {
    userId,
    onNewMessage,
    onMessageEdited,
    onMessageRemoved,
    onMemberAdded,
    onMemberRemoved,
    onUserTyping,
    onUserJoined,
    onUserLeft,
    onChatroomArchived,
    onChatroomUnarchived,
    onSettingsUpdated,
    onUnreadUpdated,
    onPresenceUpdate,
  } = options;

  // Initialize socket connection
  useEffect(() => {
    if (!userId) return;

    const socket = io(SOCKET_URL, {
      query: { userId },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Chatroom socket connected');
      // Join all user's chatrooms
      socket.emit('chatroom:join-all');
    });

    socket.on('disconnect', () => {
      console.log('Chatroom socket disconnected');
    });

    socket.on('chatroom:error', (error: any) => {
      console.error('Chatroom socket error:', error);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId]);

  // Setup event listeners
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    if (onNewMessage) {
      socket.on('chatroom:new-message', onNewMessage);
    }
    if (onMessageEdited) {
      socket.on('chatroom:message-edited', onMessageEdited);
    }
    if (onMessageRemoved) {
      socket.on('chatroom:message-removed', onMessageRemoved);
    }
    if (onMemberAdded) {
      socket.on('chatroom:member-added', onMemberAdded);
      socket.on('chatroom:member-added-you', onMemberAdded);
    }
    if (onMemberRemoved) {
      socket.on('chatroom:member-removed', onMemberRemoved);
      socket.on('chatroom:member-removed-you', onMemberRemoved);
    }
    if (onUserTyping) {
      socket.on('chatroom:user-typing', onUserTyping);
    }
    if (onUserJoined) {
      socket.on('chatroom:user-joined', onUserJoined);
    }
    if (onUserLeft) {
      socket.on('chatroom:user-left', onUserLeft);
      socket.on('chatroom:user-offline', onUserLeft);
    }
    if (onChatroomArchived) {
      socket.on('chatroom:archived', onChatroomArchived);
    }
    if (onChatroomUnarchived) {
      socket.on('chatroom:unarchived', onChatroomUnarchived);
    }
    if (onSettingsUpdated) {
      socket.on('chatroom:settings-updated', onSettingsUpdated);
    }
    if (onUnreadUpdated) {
      socket.on('chatroom:unread-updated', onUnreadUpdated);
    }
    if (onPresenceUpdate) {
      socket.on('chatroom:presence-update', onPresenceUpdate);
    }

    return () => {
      socket.off('chatroom:new-message');
      socket.off('chatroom:message-edited');
      socket.off('chatroom:message-removed');
      socket.off('chatroom:member-added');
      socket.off('chatroom:member-added-you');
      socket.off('chatroom:member-removed');
      socket.off('chatroom:member-removed-you');
      socket.off('chatroom:user-typing');
      socket.off('chatroom:user-joined');
      socket.off('chatroom:user-left');
      socket.off('chatroom:user-offline');
      socket.off('chatroom:archived');
      socket.off('chatroom:unarchived');
      socket.off('chatroom:settings-updated');
      socket.off('chatroom:unread-updated');
      socket.off('chatroom:presence-update');
    };
  }, [
    onNewMessage,
    onMessageEdited,
    onMessageRemoved,
    onMemberAdded,
    onMemberRemoved,
    onUserTyping,
    onUserJoined,
    onUserLeft,
    onChatroomArchived,
    onChatroomUnarchived,
    onSettingsUpdated,
    onUnreadUpdated,
    onPresenceUpdate,
  ]);

  // Join specific chatroom
  const joinChatroom = useCallback((roomId: string) => {
    socketRef.current?.emit('chatroom:join', { roomId });
  }, []);

  // Leave chatroom
  const leaveChatroom = useCallback((roomId: string) => {
    socketRef.current?.emit('chatroom:leave', { roomId });
  }, []);

  // Start typing
  const startTyping = useCallback((roomId: string) => {
    socketRef.current?.emit('chatroom:typing:start', { roomId });
  }, []);

  // Stop typing
  const stopTyping = useCallback((roomId: string) => {
    socketRef.current?.emit('chatroom:typing:stop', { roomId });
  }, []);

  // Mark messages as read
  const markAsRead = useCallback((roomId: string) => {
    socketRef.current?.emit('chatroom:mark-read', { roomId });
  }, []);

  // Update presence
  const updatePresence = useCallback((roomId: string) => {
    socketRef.current?.emit('chatroom:presence', { roomId });
  }, []);

  return {
    socket: socketRef.current,
    joinChatroom,
    leaveChatroom,
    startTyping,
    stopTyping,
    markAsRead,
    updatePresence,
  };
}
